import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Loader2, Calendar, ArrowLeft, X } from "lucide-react";
import { useT, useLanguage } from "../lib/LanguageContext";
import { postView } from "../lib/blogLanguage";
import { formatBlogDate } from "../lib/blogDates";
import { markdownToPlainText } from "../lib/markdown";
import MarkdownBody from "../components/MarkdownBody";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const PAGE_SIZE = 12;
const DESCRIPTION_MAX = 160;

/**
 * Appends a freshly fetched page to what is shown, by `post_id`.
 *
 * "Load more" pages with `skip` = posts already shown, newest first. A post published
 * between two clicks lands at the top and pushes every older one down a place, so the next
 * page repeats the last post already on screen: dropping rows whose `post_id` is already
 * shown makes that harmless (and keeps the React keys unique), and nothing is skipped,
 * because the new page still starts at or before the first unseen post. The opposite race
 * (a shown post unpublished between clicks) pulls the list up and can hide ONE unseen post
 * until the next fresh load; a keyset cursor on (published_at, post_id) would remove that,
 * and is not worth an API change for a blog.
 * A page that adds nothing means the list is exhausted, so the control is retired by
 * clamping `total` to what is shown rather than leaving a button that does nothing.
 * `shown` is null while a query's first page is loading; a page then is the whole list.
 * (A page of a SUPERSEDED query never reaches here: `SolunaryBlog` discards it.)
 */
export function mergePage(shown, next) {
  const shownItems = shown ? shown.items : [];
  const seen = new Set(shownItems.map((p) => p.post_id));
  const fresh = next.items.filter((p) => !seen.has(p.post_id));
  const items = [...shownItems, ...fresh];
  return { total: next.items.length === 0 ? items.length : next.total, items };
}

/** `/blog?tag=<tag>`: the list filtered to the posts carrying that tag in any language. */
const tagHref = (tag) => `/blog?${new URLSearchParams({ tag })}`;

/**
 * Sets the document title and the meta description for the lifetime of the page, so
 * in-app navigation carries the right tab title and share text. Crawlers read the
 * server-rendered head instead (contract section 5); this covers readers already in the SPA.
 */
function useDocumentMeta(title, description) {
  useEffect(() => {
    if (!title) return undefined;
    const previousTitle = document.title;
    let meta = document.querySelector('meta[name="description"]');
    const created = !meta;
    if (created) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }
    const previousDescription = meta.getAttribute("content");
    document.title = title;
    meta.setAttribute("content", description || "");
    return () => {
      document.title = previousTitle;
      if (created) meta.remove();
      else if (previousDescription === null) meta.removeAttribute("content");
      else meta.setAttribute("content", previousDescription);
    };
  }, [title, description]);
}

function TagLink({ tag, testId, className }) {
  return <Link to={tagHref(tag)} className={className} data-testid={testId}>{tag}</Link>;
}

export function SolunaryBlog() {
  const [searchParams] = useSearchParams();
  const tag = searchParams.get("tag") || "";
  // `null` = first page still loading; afterwards `{ total, items }`.
  const [data, setData] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  // The generation of the query on screen: bumped each time the first page is (re)fetched
  // (a tag chosen or cleared). A "Load more" answer that arrives under another generation
  // belongs to a query the reader has left and is dropped, never merged into the new list.
  const generation = useRef(0);
  const t = useT("saas");
  const tc = useT("common");
  const { lang } = useLanguage();

  const fetchPage = useCallback(async (skip) => {
    const params = { ...(tag ? { tag } : {}), limit: PAGE_SIZE, skip };
    const r = await axios.get(`${API}/public/solunary/blog`, { params });
    // A non-list body (the production proxy answers 200 + index.html for an unknown /api
    // path) is "nothing to show", never a crash.
    return Array.isArray(r.data?.items) ? r.data : { total: 0, items: [] };
  }, [tag]);

  useEffect(() => {
    let live = true;
    generation.current += 1;
    setData(null);
    setLoadingMore(false);
    fetchPage(0).then((d) => { if (live) setData(d); }).catch(() => { if (live) setData({ total: 0, items: [] }); });
    return () => { live = false; };
  }, [fetchPage]);

  const loadMore = async () => {
    const mine = generation.current;
    setLoadingMore(true);
    try {
      const next = await fetchPage(data.items.length);
      if (generation.current === mine) setData((d) => mergePage(d, next));
    } catch {
      // The posts already shown stay; the control remains so the reader can retry.
    } finally {
      if (generation.current === mine) setLoadingMore(false);
    }
  };

  useDocumentMeta(t("blog.pageTitle"), t("blog.pageSubtitle"));

  return (
    <div className="min-h-screen bg-sol-midnight text-white">
      <div className="max-w-5xl mx-auto px-6 py-20">
        <Link to="/" className="text-sm text-white/40 hover:text-sol-gold inline-flex items-center gap-2 mb-8">
          <ArrowLeft className="w-4 h-4" /> {tc("actions.back")}
        </Link>
        <h1 className="font-display font-semibold text-6xl mb-4 bg-gradient-to-r from-sol-gold via-rose-200 to-sol-sage bg-clip-text text-transparent">
          {t("blog.pageTitle")}
        </h1>
        <p className="text-white/60 text-lg mb-10">
          {t("blog.pageSubtitle")}
        </p>

        {tag && (
          <div className="mb-10 flex items-center gap-3" data-testid="blog-tag-filter">
            <span className="text-sm text-white/60">{t("blog.filteredBy", { tag })}</span>
            <Link to="/blog" className="text-xs text-sol-gold border border-sol-gold/40 rounded-full px-3 py-1 inline-flex items-center gap-1 hover:bg-sol-gold/10" data-testid="blog-tag-clear">
              <X className="w-3 h-3" /> {t("blog.clearFilter")}
            </Link>
          </div>
        )}

        {!data ? <Loader2 className="w-6 h-6 animate-spin text-sol-gold" /> :
         data.items.length === 0 ? <p className="text-white/40">{t("blog.empty")}</p> : (
          <>
            <div className="grid md:grid-cols-2 gap-8">
              {data.items.map((p) => {
                const v = postView(p, lang);
                return (
                <article key={p.post_id} className="group relative rounded-2xl bg-white/[0.03] border border-white/10 overflow-hidden hover:border-sol-gold/30 transition" data-testid={`blog-card-${p.slug}`}>
                  {p.cover_url && <img src={p.cover_url} alt={v.title} className="w-full h-48 object-cover" onError={(e) => { e.target.style.display = "none"; }} />}
                  <div className="p-6">
                    {v.tag && (
                      // Above the stretched title link (z-10) so the chip is its own target.
                      <TagLink tag={v.tag} testId={`blog-tag-chip-${p.slug}`} className="relative z-10 inline-block text-xs text-sol-gold uppercase tracking-wider mb-2 hover:underline" />
                    )}
                    <h3 className="font-display text-2xl group-hover:text-sol-gold transition mb-2">
                      <Link to={`/blog/${p.slug}`} className="after:absolute after:inset-0" data-testid={`blog-link-${p.slug}`}>{v.title}</Link>
                    </h3>
                    <p className="text-sm text-white/60 mb-4 line-clamp-2">{v.excerpt}</p>
                    <div className="text-xs text-white/40 flex items-center gap-1.5">
                      <Calendar className="w-3 h-3" />
                      {formatBlogDate(p.published_at, lang, "long")}
                    </div>
                  </div>
                </article>
                );
              })}
            </div>
            {data.items.length < data.total && (
              <div className="mt-12 text-center">
                <button type="button" onClick={loadMore} disabled={loadingMore} className="text-sm border border-white/20 rounded-full px-6 py-2 hover:border-sol-gold hover:text-sol-gold transition disabled:opacity-50" data-testid="blog-load-more">
                  {t("blog.loadMore")}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export function SolunaryBlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [failed, setFailed] = useState(false);
  const t = useT("saas");
  const { lang } = useLanguage();

  useEffect(() => {
    let live = true;
    setPost(null);
    setFailed(false);
    axios.get(`${API}/public/solunary/blog/${slug}`)
      .then((r) => { if (live) setPost(r.data); })
      .catch(() => { if (live) setFailed(true); });
    return () => { live = false; };
  }, [slug]);

  const { title, excerpt, tag, body } = postView(post, lang);
  const description = excerpt || markdownToPlainText(body).slice(0, DESCRIPTION_MAX);
  useDocumentMeta(title, description);

  if (failed) return <div className="min-h-screen bg-sol-midnight text-white flex items-center justify-center"><p>{t("blog.notFound")}</p></div>;
  if (!post) return <div className="min-h-screen bg-sol-midnight flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-sol-gold" /></div>;

  return (
    <div className="min-h-screen bg-sol-midnight text-white">
      <article className="max-w-3xl mx-auto px-6 py-20">
        <Link to="/blog" className="text-sm text-white/40 hover:text-sol-gold inline-flex items-center gap-2 mb-8" data-testid="back-to-blog">
          <ArrowLeft className="w-4 h-4" /> {t("blog.allPosts")}
        </Link>
        {tag && <div className="mb-4"><TagLink tag={tag} testId="post-tag" className="text-xs text-sol-gold uppercase tracking-wider hover:underline" /></div>}
        <h1 className="font-display font-semibold text-5xl md:text-6xl mb-4 leading-tight">{title}</h1>
        {excerpt && <p className="text-white/60 text-lg mb-8">{excerpt}</p>}
        <div className="text-xs text-white/40 mb-8 flex items-center gap-1.5">
          <Calendar className="w-3 h-3" /> {formatBlogDate(post.published_at, lang, "long")}
        </div>
        {post.cover_url && <img src={post.cover_url} alt={title} className="w-full rounded-2xl mb-10" onError={(e) => { e.target.style.display = "none"; }} />}
        <MarkdownBody source={body} tone="dark" testId="post-body" />
      </article>
    </div>
  );
}
